import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, CheckCircle2, Clock, Copy, FileSignature, Loader2, Percent, Plus, Search, Send } from 'lucide-react';
import { format, parseISO, startOfMonth } from 'date-fns';
import { toast } from 'sonner';

import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useMarkQuoteSent, useQuoteLinkBuilder, useQuotes, type QuoteListRow } from '@/hooks/useQuotes';
import { QUOTE_STATUSES, effectiveQuoteStatus, formatQuoteMoney, type QuoteStatus } from '@/lib/quotes';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPage,
  EmptyState,
  FilterChip,
  PageHeader,
  Panel,
  PersonAvatar,
  StatCard,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  adminButton,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { VatSettingsDialog } from '@/components/admin/quotes/VatSettingsDialog';

const VIOLET = 'hsl(var(--adm-violet))';
const MINT = 'hsl(172 46% 48%)';
const SUN = 'hsl(42 95% 52%)';
const BLUE = 'hsl(214 84% 58%)';

const shortDate = (value: string | null) => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'dd MMM yyyy');
  } catch {
    return value;
  }
};

/** Total across mixed currencies would mislead, so KPIs use the most common one. */
function sumIn(rows: QuoteListRow[], currency: string) {
  return rows.filter((r) => r.currency_code === currency).reduce((acc, r) => acc + r.total_amount, 0);
}

const QuotesAdmin = () => {
  useAdminPageTitle('Quotes');
  const navigate = useNavigate();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { data: quotes = [], isLoading } = useQuotes(orgId);
  const buildLink = useQuoteLinkBuilder(orgId);
  const markSent = useMarkQuoteSent();
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [vatOpen, setVatOpen] = useState(false);

  const rows = useMemo(
    () => quotes.map((q) => ({ ...q, effective: effectiveQuoteStatus(q.status, q.valid_until) })),
    [quotes],
  );

  const summary = useMemo(() => {
    const counts: Partial<Record<QuoteStatus, number>> = {};
    for (const r of rows) counts[r.effective] = (counts[r.effective] ?? 0) + 1;
    const currencyCounts = new Map<string, number>();
    for (const r of rows) currencyCounts.set(r.currency_code, (currencyCounts.get(r.currency_code) ?? 0) + 1);
    const currency = [...currencyCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'GBP';
    const open = rows.filter((r) => r.effective === 'sent' || r.effective === 'viewed');
    const monthStart = startOfMonth(new Date()).getTime();
    const acceptedThisMonth = rows.filter(
      (r) => r.effective === 'accepted' && r.accepted_at && new Date(r.accepted_at).getTime() >= monthStart,
    );
    const decided = (counts.accepted ?? 0) + (counts.declined ?? 0) + (counts.expired ?? 0);
    const winRate = decided > 0 ? Math.round(((counts.accepted ?? 0) / decided) * 100) : null;
    const toBook = rows.filter((r) => r.effective === 'accepted' && !r.job_id).length;
    return {
      counts,
      currency,
      openValue: sumIn(open, currency),
      openCount: open.length,
      acceptedValue: sumIn(acceptedThisMonth, currency),
      acceptedCount: acceptedThisMonth.length,
      winRate,
      toBook,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.effective !== statusFilter) return false;
      if (!term) return true;
      return [r.quote_number, r.client_name, r.client_email].some((v) => v?.toLowerCase().includes(term));
    });
  }, [rows, statusFilter, search]);

  const copyLink = async (quote: QuoteListRow) => {
    if (quote.status === 'draft') {
      if (!assertCanWrite() || !orgId) return;
      await markSent.mutateAsync({ quoteId: quote.id, organizationId: orgId, enquiryId: quote.enquiry_id });
    }
    try {
      await navigator.clipboard.writeText(buildLink(quote.public_token));
      toast.success(quote.status === 'draft' ? 'Link copied — quote marked as sent' : 'Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const newQuoteButton = (
    <Button size="sm" className={adminButton.primary} disabled={!canWrite} onClick={() => navigate('/admin/quotes/new')}>
      <Plus />
      New quote
    </Button>
  );

  return (
    <AdminPage>
      <PageHeader
        description="Price up a job, send it, and let your customer accept online."
        actions={
          <>
            <Button size="sm" variant="ghost" className={adminButton.outline} onClick={() => setVatOpen(true)}>
              <Percent />
              VAT settings
            </Button>
            {newQuoteButton}
          </>
        }
      />

      {quotes.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          <StatCard
            label="Waiting for a reply"
            icon={<Send />}
            value={formatQuoteMoney(summary.openValue, summary.currency)}
            hint={`${summary.openCount} open quote${summary.openCount === 1 ? '' : 's'}`}
            color={BLUE}
          />
          <StatCard
            label="Accepted this month"
            icon={<CheckCircle2 />}
            value={formatQuoteMoney(summary.acceptedValue, summary.currency)}
            hint={`${summary.acceptedCount} quote${summary.acceptedCount === 1 ? '' : 's'}`}
            color={MINT}
          />
          <StatCard
            label="Win rate"
            icon={<FileSignature />}
            value={summary.winRate == null ? '—' : `${summary.winRate}%`}
            hint="accepted vs declined or expired"
            color={VIOLET}
          />
          <StatCard
            label="Ready to book"
            icon={<CalendarPlus />}
            value={summary.toBook}
            hint="accepted, not booked in yet"
            color={SUN}
          />
        </div>
      )}

      <Panel
        title="All quotes"
        description={`${quotes.length} quote${quotes.length === 1 ? '' : 's'}`}
        padded={false}
      >
        {quotes.length > 0 && (
          <Toolbar className="border-b border-border px-4 pb-3 pt-3 md:px-5">
            <div
              className="adm-scroll -mx-4 flex w-[calc(100%+2rem)] items-center gap-1.5 overflow-x-auto px-4 pb-0.5 sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
              role="group"
              aria-label="Filter by status"
            >
              <FilterChip active={statusFilter === 'all'} count={quotes.length} onClick={() => setStatusFilter('all')}>
                All
              </FilterChip>
              {QUOTE_STATUSES.filter((s) => (summary.counts[s.value] ?? 0) > 0 || ['draft', 'sent', 'accepted'].includes(s.value)).map((s) => (
                <FilterChip
                  key={s.value}
                  active={statusFilter === s.value}
                  count={summary.counts[s.value] ?? 0}
                  onClick={() => setStatusFilter(statusFilter === s.value ? 'all' : s.value)}
                >
                  {s.label}
                </FilterChip>
              ))}
            </div>
            <ToolbarSpacer />
            <div className="adm-search h-9 w-full sm:w-[240px]">
              <Search className="h-4 w-4 shrink-0" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search number or customer"
                aria-label="Search quotes"
              />
            </div>
          </Toolbar>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : quotes.length === 0 ? (
          <EmptyState
            icon={<FileSignature />}
            title="No quotes yet"
            description="Create one from an enquiry, a customer, or start from scratch."
            action={newQuoteButton}
          />
        ) : visible.length === 0 ? (
          <EmptyState compact icon={<Search />} title="No matching quotes" description="Try another status or search." />
        ) : (
          <div className="overflow-x-auto">
            <Table className="adm-table min-w-[680px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Quote</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Valid until</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="pr-5 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((quote) => (
                  <TableRow key={quote.id} data-testid={`quote-row-${quote.quote_number}`}>
                    <TableCell className="pl-5">
                      <Link to={`/admin/quotes/${quote.id}`} className="block min-w-0 hover:underline">
                        <p className="adm-num whitespace-nowrap font-semibold text-foreground">{quote.quote_number}</p>
                        <p className="whitespace-nowrap text-[11.5px] text-muted-foreground">
                          Created {shortDate(quote.created_at)}
                        </p>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-0 items-center gap-2">
                        <PersonAvatar name={quote.client_name} size={26} />
                        <span className="truncate font-medium text-foreground">{quote.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <StatusChip status={quote.effective} />
                        {quote.effective === 'accepted' && !quote.job_id && (
                          <span className="text-[11.5px] font-medium text-muted-foreground">to book</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap text-muted-foreground md:table-cell">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        {shortDate(quote.valid_until)}
                      </span>
                    </TableCell>
                    <TableCell className="adm-num text-right font-semibold text-foreground">
                      {formatQuoteMoney(quote.total_amount, quote.currency_code)}
                    </TableCell>
                    <TableCell className="pr-5 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={adminButton.iconGhost}
                          aria-label={`Copy link for ${quote.quote_number}`}
                          title="Copy link"
                          disabled={quote.status === 'draft' && !canWrite}
                          onClick={() => copyLink(quote)}
                        >
                          <Copy />
                        </Button>
                        <Button variant="ghost" size="sm" className={adminButton.outline} asChild>
                          <Link to={`/admin/quotes/${quote.id}`}>Open</Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <VatSettingsDialog orgId={orgId} open={vatOpen} onOpenChange={setVatOpen} canWrite={canWrite} />
    </AdminPage>
  );
};

export default QuotesAdmin;
